import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import TagMigrationService from "./TagMigrationService";

const SOURCE_OPTIONS = [
    { value: "BEDROCK", label: "Bedrock idea", countKey: "bedrockCount" },
    { value: "WORD_PUZZLE", label: "Word puzzles", countKey: "wordPuzzleCount" },
    { value: "JIGSAW", label: "Jigsaw", countKey: "jigsawCount", publishedKey: "jigsawPublishedCount" },
    { value: "SLIDING", label: "Sliding", countKey: "slidingCount", publishedKey: "slidingPublishedCount" },
];

function emptyRow(tag) {
    return {
        tag: tag || "",
        bedrockCount: 0,
        wordPuzzleCount: 0,
        jigsawCount: 0,
        jigsawPublishedCount: 0,
        slidingCount: 0,
        slidingPublishedCount: 0,
        totalCount: 0,
    };
}

function countForSource(row, source) {
    if (source === "BEDROCK") return Number(row?.bedrockCount) || 0;
    if (source === "WORD_PUZZLE") return Number(row?.wordPuzzleCount) || 0;
    if (source === "JIGSAW") return Number(row?.jigsawCount) || 0;
    if (source === "SLIDING") return Number(row?.slidingCount) || 0;
    return Number(row?.totalCount) || 0;
}

function defaultSourcesForRow(row) {
    return SOURCE_OPTIONS.filter((s) => countForSource(row, s.value) > 0).map((s) => s.value);
}

function applySourceLabel(opt, row) {
    const count = row ? countForSource(row, opt.value) : 0;
    if (!opt.publishedKey) {
        return `${opt.label} (${count})`;
    }
    const published = Number(row?.[opt.publishedKey]) || 0;
    return `${opt.label} (${count} · ${published} live on app)`;
}

/**
 * Same rename dialog as /admin/system/tag-migration.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} [initialFromTag] — prefill current tag (e.g. selected filter tag)
 * @param {object} [initialRow] — migration row with counts; when omitted, loaded from the API
 * @param {string} [language] — english | french | "" for all
 * @param {(result: { from: string, to: string, updated?: object }) => void} [onRenamed]
 * @param {(msg: { severity: string, message: string }) => void} [onMessage]
 */
export default function TagRenameDialog({
    open,
    onClose,
    initialFromTag = "",
    initialRow = null,
    language = "",
    onRenamed,
    onMessage,
}) {
    const [loadingRow, setLoadingRow] = useState(false);
    const [renameRow, setRenameRow] = useState(null);
    const [tagOptions, setTagOptions] = useState([]);
    const [toTag, setToTag] = useState("");
    const [renameSources, setRenameSources] = useState([]);
    const [renaming, setRenaming] = useState(false);
    const [renameStories, setRenameStories] = useState({ JIGSAW: [], SLIDING: [] });
    const [renameStoriesLoading, setRenameStoriesLoading] = useState(false);
    const [fromEditable, setFromEditable] = useState(false);

    const languageFilter = typeof language === "string" ? language.trim() : "";

    useEffect(() => {
        if (!open) {
            setRenameRow(null);
            setToTag("");
            setRenameSources([]);
            setTagOptions([]);
            setFromEditable(false);
            setRenameStories({ JIGSAW: [], SLIDING: [] });
            setRenameStoriesLoading(false);
            setLoadingRow(false);
            return undefined;
        }

        let cancelled = false;
        const boot = async () => {
            setLoadingRow(true);
            setToTag("");
            const seed = String(initialFromTag || initialRow?.tag || "").trim();
            if (initialRow && initialRow.tag) {
                if (!cancelled) {
                    setRenameRow(initialRow);
                    setRenameSources(defaultSourcesForRow(initialRow));
                    setFromEditable(false);
                }
            } else if (seed) {
                if (!cancelled) {
                    setRenameRow(emptyRow(seed));
                    setRenameSources(["WORD_PUZZLE"]);
                    setFromEditable(false);
                }
            } else {
                if (!cancelled) {
                    setRenameRow(emptyRow(""));
                    setRenameSources(["WORD_PUZZLE"]);
                    setFromEditable(true);
                }
            }

            const result = await TagMigrationService.list(languageFilter);
            if (cancelled) return;
            if (!result.success) {
                onMessage?.({
                    severity: "error",
                    message: result.error || "Failed to load tags",
                });
                setLoadingRow(false);
                return;
            }
            const rows = Array.isArray(result.data) ? result.data : [];
            setTagOptions(
                rows
                    .map((r) => r.tag)
                    .filter((t) => typeof t === "string" && t.length > 0)
                    .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" })),
            );
            if (seed) {
                const match = rows.find(
                    (r) => String(r.tag || "").trim().toLowerCase() === seed.toLowerCase(),
                );
                if (match) {
                    setRenameRow(match);
                    setRenameSources(defaultSourcesForRow(match));
                    setFromEditable(false);
                }
            }
            setLoadingRow(false);
        };
        boot();
        return () => {
            cancelled = true;
        };
        // Intentionally omit onMessage / initialRow identity churn; re-open via `open` + fromTag/language.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFromTag, languageFilter, initialRow?.tag]);

    useEffect(() => {
        if (!open || !renameRow?.tag) {
            setRenameStories({ JIGSAW: [], SLIDING: [] });
            setRenameStoriesLoading(false);
            return undefined;
        }
        let cancelled = false;
        const load = async () => {
            setRenameStoriesLoading(true);
            const next = { JIGSAW: [], SLIDING: [] };
            if (countForSource(renameRow, "JIGSAW") > 0) {
                const result = await TagMigrationService.listStories(renameRow.tag, "JIGSAW", languageFilter);
                if (!cancelled && result.success) {
                    next.JIGSAW = result.data;
                } else if (!cancelled && !result.success) {
                    onMessage?.({
                        severity: "error",
                        message: result.error || "Failed to load jigsaw stories for this tag",
                    });
                }
            }
            if (countForSource(renameRow, "SLIDING") > 0) {
                const result = await TagMigrationService.listStories(renameRow.tag, "SLIDING", languageFilter);
                if (!cancelled && result.success) {
                    next.SLIDING = result.data;
                } else if (!cancelled && !result.success) {
                    onMessage?.({
                        severity: "error",
                        message: result.error || "Failed to load sliding stories for this tag",
                    });
                }
            }
            if (!cancelled) {
                setRenameStories(next);
                setRenameStoriesLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, renameRow?.tag, languageFilter, renameRow?.jigsawCount, renameRow?.slidingCount]);

    const handlePickFromTag = async (value) => {
        const next = String(value || "").trim();
        if (!next) {
            setRenameRow(emptyRow(""));
            setRenameSources(["WORD_PUZZLE"]);
            return;
        }
        setRenameRow(emptyRow(next));
        const result = await TagMigrationService.list(languageFilter);
        if (result.success) {
            const match = (result.data || []).find(
                (r) => String(r.tag || "").trim().toLowerCase() === next.toLowerCase(),
            );
            if (match) {
                setRenameRow(match);
                setRenameSources(defaultSourcesForRow(match));
                return;
            }
        }
        setRenameSources(["WORD_PUZZLE"]);
    };

    const handleRename = async () => {
        const from = String(renameRow?.tag || "").trim();
        const next = String(toTag || "").trim();
        if (!from || !next) {
            onMessage?.({ severity: "warning", message: "Enter the new tag name" });
            return;
        }
        if (from.toLowerCase() === next.toLowerCase()) {
            onMessage?.({ severity: "warning", message: "New name must be different" });
            return;
        }
        if (renameSources.length === 0) {
            onMessage?.({ severity: "warning", message: "Select at least one source" });
            return;
        }
        setRenaming(true);
        const result = await TagMigrationService.rename(from, next, renameSources, languageFilter);
        setRenaming(false);
        if (!result.success) {
            onMessage?.({ severity: "error", message: result.error || "Rename failed" });
            return;
        }
        const updated = result.data?.updated || {};
        const total = Number(updated.total) || 0;
        onMessage?.({
            severity: "success",
            message: total === 1 ? "Updated 1 record" : `Updated ${total} records`,
        });
        onRenamed?.({ from, to: next, updated });
        onClose?.();
    };

    const currentTagValue = renameRow?.tag || "";
    const optionsExcludingCurrent = useMemo(
        () =>
            tagOptions
                .filter((t) => t !== currentTagValue)
                .slice()
                .sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" })),
        [tagOptions, currentTagValue],
    );

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!renaming) onClose?.();
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Rename tag</DialogTitle>
            <DialogContent>
                {loadingRow ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {renameRow && (Number(renameRow.jigsawCount) || 0) > (Number(renameRow.jigsawPublishedCount) || 0) ? (
                            <Alert severity="info">
                                Jigsaw {renameRow.jigsawCount} includes unpublished stories. Only{" "}
                                {Number(renameRow.jigsawPublishedCount) || 0} are live on the app, so this tag can be
                                missing from the player tag list.
                            </Alert>
                        ) : null}
                        {fromEditable ? (
                            <Autocomplete
                                freeSolo
                                options={tagOptions}
                                value={currentTagValue}
                                onInputChange={(_e, value) => handlePickFromTag(value)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Current tag"
                                        placeholder="Pick or type the tag to rename"
                                        size="small"
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                label="Current tag"
                                value={currentTagValue}
                                size="small"
                                fullWidth
                                slotProps={{ input: { readOnly: true } }}
                            />
                        )}
                        <Autocomplete
                            freeSolo
                            options={optionsExcludingCurrent}
                            value={toTag}
                            onInputChange={(_e, value) => setToTag(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="New tag name"
                                    placeholder="Type a new name or pick an existing tag"
                                    size="small"
                                />
                            )}
                        />
                        <FormGroup>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Apply to
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                                Jigsaw and Sliding totals count any story that still has this tag and a puzzle row. Live
                                on app means a published bundle (jigsaw also needs an enabled visible template).
                            </Typography>
                            {SOURCE_OPTIONS.map((opt) => {
                                const count = renameRow ? countForSource(renameRow, opt.value) : 0;
                                const stories =
                                    opt.value === "JIGSAW" || opt.value === "SLIDING"
                                        ? renameStories[opt.value] || []
                                        : [];
                                return (
                                    <Box key={opt.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={renameSources.includes(opt.value)}
                                                    onChange={(_, checked) => {
                                                        setRenameSources((prev) =>
                                                            checked
                                                                ? [...prev, opt.value]
                                                                : prev.filter((v) => v !== opt.value),
                                                        );
                                                    }}
                                                />
                                            }
                                            label={applySourceLabel(opt, renameRow)}
                                        />
                                        {count > 0 && (opt.value === "JIGSAW" || opt.value === "SLIDING") ? (
                                            <Box sx={{ pl: 4, pb: 1 }}>
                                                {renameStoriesLoading ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Loading stories…
                                                    </Typography>
                                                ) : stories.length === 0 ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        Could not load the story list.
                                                    </Typography>
                                                ) : (
                                                    stories.map((story) => (
                                                        <Stack key={story.storyId} spacing={0} sx={{ mb: 0.75 }}>
                                                            <Link
                                                                component={RouterLink}
                                                                to={story.adminPath}
                                                                underline="hover"
                                                                variant="body2"
                                                            >
                                                                {story.title}
                                                            </Link>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {story.liveOnApp
                                                                    ? "Live on app"
                                                                    : story.bundleState || "no bundle"}
                                                                {story.standaloneImage ? " · standalone image" : ""}
                                                                {Array.isArray(story.tagOn) && story.tagOn.length > 0
                                                                    ? ` · tag on ${story.tagOn.join(", ")}`
                                                                    : ""}
                                                            </Typography>
                                                        </Stack>
                                                    ))
                                                )}
                                            </Box>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </FormGroup>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={renaming}>
                    Cancel
                </Button>
                <Button onClick={handleRename} variant="contained" disabled={renaming || loadingRow}>
                    {renaming ? <CircularProgress size={22} /> : "Rename"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
